import { createContext, useContext, ReactNode, useEffect } from 'react'
import { useHallUser, useHallUsers } from './HallUsersProvider'
import { useSocket } from './SocketProvider'
import { useAssemblyLiveTranscription } from '@/hooks/use-assembly-live-transcription'
import { useLiveAudioStream } from '@/hooks/use-live-stream-audio'
import { useResetPubSub } from '@/hooks/pub-sub/use-reset-pub-sub'
import { useAudioRecorder } from '@/hooks/use-audio-recorder'
import { useTurns } from './TurnsProvider'
import { TurnStatus, TurnType } from '@db8/shared'
import { useAssembly } from '@/hooks/use-assembly'
import { useHall } from './HallProvider'
import { useAzureTts } from './AzureTtsProvider'
import { useFlowNotes } from '@/contexts/FlowNotesProvider'
import { useUser } from '@clerk/clerk-react'

interface MiscContextProps {
  reset: (any) => void
}

// * Misc functions for edge cases for data management between mutually dependent providers, since A cannot rely on B while B relies on A.

const MiscContext = createContext<MiscContextProps | undefined>(undefined)

export const MiscProvider = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useHallUsers()
  const { startTranscription, sendAudioChunk, stopTranscription } = useAssemblyLiveTranscription()
  const { startStream, stopStream } = useLiveAudioStream()
  const { reset } = useResetPubSub()
  const { currentTurn, resetTimer } = useTurns()
  const { initializeAudioRecorder, stopRecordingAndGetFile, continueRecording } = useAudioRecorder()
  const { getAudioTranscription } = useAssembly()
  const { updateTranscript, setLocalMetadata, hallMetadata, lastSender } = useHall()
  const { fetchAudio, playReading, pauseReading, audioRef } = useAzureTts()
  const { fetchFlowNote } = useFlowNotes()

  const getTtsAudio = async (activeTts) => {
    await fetchAudio(activeTts)
  }

  // === Manual audio control only - no automatic play/pause based on status

  useEffect(() => {
    if (hallMetadata?.activeTts && hallMetadata.activeTts.trim() !== '') {
      // Pause any currently playing audio before fetching new audio
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      getTtsAudio(hallMetadata.activeTts)
    } else if (audioRef.current) {
      // Clear audio when activeTts is empty (new turn)
      audioRef.current.pause()
      audioRef.current = null
    }
  }, [hallMetadata.activeTts])

  useEffect(() => {
    if (!audioRef.current) return
    const targetTime = hallMetadata.ttsTime ?? 0
    const diff = Math.abs(audioRef.current.currentTime - targetTime)

    if (diff > 0.25) {
      audioRef.current.currentTime = targetTime
    }
  }, [hallMetadata.ttsTime])

  useSocket({
    'start-user-transcription': async ({ users }) => {
      if (users.includes(currentUser.id) && currentTurn) {
        // Never record / generate transcripts for prep time turns.
        if (currentTurn.type === TurnType.Preperatory) return
        if (
          [TurnType.AffirmativeCrossNegative, TurnType.NegativeCrossAffirmative].includes(
            currentTurn.type,
          )
        ) {
          startTranscription()
          await startStream((chunk) => sendAudioChunk(chunk))
        } else {
          initializeAudioRecorder(currentUser.videoSDKId)
          continueRecording()
        }
      }
    },
    'stop-user-transcription': async ({ users }) => {
      if (users.includes(currentUser.id) && currentTurn) {
        // Never record / generate transcripts for prep time turns.
        if (currentTurn.type === TurnType.Preperatory) return
        if (
          [TurnType.AffirmativeCrossNegative, TurnType.NegativeCrossAffirmative].includes(
            currentTurn.type,
          )
        ) {
          stopTranscription()
          stopStream()
        } else {
          const file = await stopRecordingAndGetFile()
          if (file) {
            const res = await getAudioTranscription(file)
            const text: string = res?.text || ''
            updateTranscript(currentTurn?.step, {
              text,
              hallUser: currentUser,
              turnStep: currentTurn.step,
              id: `transcript-${Date.now()}-${Math.random()}`,
              role: currentUser.speakingRole,
            })
            // Immediately generate flow notes using the fresh transcript to avoid state-race
            try {
              await fetchFlowNote({ transcript: text, step: currentTurn.step, turnType: currentTurn.type })
            } catch {}
          }
        }
      }
    },
    'crossfire-play-tts': async ({ activeTts }) => {
      if (!activeTts) return
      // Do NOT fetch here: hallMetadata.activeTts update already triggers fetchAudio via the existing effect above.
      // We only wait until the audio element is ready, then attempt best-effort autoplay.
      const start = Date.now()
      const timeoutMs = 6000
      const pollMs = 120
      while (Date.now() - start < timeoutMs) {
        const el = audioRef.current as HTMLAudioElement | null
        if (el && el.src) {
          try {
            playReading()
          } catch {}
          break
        }
        await new Promise((r) => setTimeout(r, pollMs))
      }
    },
    'crossfire-stop-tts': async () => {
      try {
        pauseReading()
      } catch {}
    },
  })

  return (
    <MiscContext.Provider
      value={{
        reset,
      }}
    >
      {children}
    </MiscContext.Provider>
  )
}

export const useMisc = () => {
  const context = useContext(MiscContext)
  if (!context) {
    throw new Error('useMisc must be used within a MiscProvider')
  }
  return context
}
